<template>
    <div>
        <vue-headful :title="'[Admin] Settings - AMOS365'" />

        <h4 class="font-weight-bold py-3 mb-4">
            <span class="text-muted font-weight-light">Admin /</span> Settings
        </h4>

        <ul class="search-nav nav nav-tabs tabs-alt container-m--x mt-4 d-lg-flex d-none">
            <b-nav-item :active="curTab === item.key" @click="curTab = item.key" v-for="item in tabs" :key="item.key">
                <i :class="`fa fa-${item.icon}`"></i> {{ item.title }}
            </b-nav-item>
        </ul>

        <div class="mb-2 mb-lg-0 d-lg-none d-block">
            <b-dropdown :html="tabTitle" variant="default" class="bg-white">
                <b-dropdown-item :active="curTab === item.key" @click="curTab = item.key" v-for="item in tabs" :key="item.key">
                    <i :class="`fa fa-${item.icon}`"></i> {{ item.title }}
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <div class="pt-2">
            <div v-show="curTab === 'account'">
                <div class="row">
                    <div class="col-lg-8">
                        <div class="card mb-3">
                            <div class="card-header">
                                <strong>Account Settings</strong>
                            </div>
                            <div class="card-body">
                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right" :class="{'text-danger': errors.account_name, 'text-muted-no': !errors.account_name}">
                                        Company Name:
                                    </div>
                                    <div class="col-md-8 col-12 input-group-sm">
                                        <input type="text" v-model="settings.account_name" :class="{'form-control': true, 'is-invalid': errors.account_name}" placeholder="Enter Company Name" />

                                        <div class="text-danger mt-1" v-if="errors.account_name">
                                            <ul class="list-unstyled mb-0">
                                                <li v-for="err in errors.account_name">{{ err }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right" :class="{'text-danger': errors.reply_to_email, 'text-muted-no': !errors.reply_to_email}">
                                        Reply-To Email:
                                    </div>
                                    <div class="col-md-8 col-12 input-group-sm">
                                        <input type="text" v-model="settings.reply_to_email" :class="{'form-control': true, 'is-invalid': errors.reply_to_email}" placeholder="Enter Reply-To Email" />

                                        <div class="text-danger mt-1" v-if="errors.reply_to_email">
                                            <ul class="list-unstyled mb-0">
                                                <li v-for="err in errors.reply_to_email">{{ err }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card mb-3">
                            <div class="card-header">
                                <strong>Downline Report Settings</strong>
                            </div>
                            <div class="card-body">
                                <div class="row mb-3">
                                    <div class="offset-md-1 offset-0"></div>
                                    <div class="col-md-10 col-12 input-group-sm">
                                        <div class="mb-1" :class="{'text-danger': errors.txt_downline_help, 'text-muted-no': !errors.txt_downline_help}">
                                            Summary Help Text:
                                        </div>
                                        <vue-editor v-model="settings.txt_downline_help" :editorOptions="editorSettings" :editor-toolbar="customToolbar"></vue-editor>

                                        <div class="small text-info">This text will be prepended to the Downline Report Exports.</div>

                                        <div class="text-danger mt-1" v-if="errors.txt_downline_help">
                                            <ul class="list-unstyled mb-0">
                                                <li v-for="err in errors.txt_downline_help">{{ err }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button type="button" class="btn btn-sm btn-success" v-on:click="postSettings()">
                            Save Settings
                        </button>
                    </div>
                </div>
            </div>

            <div v-show="curTab === 'product_types'">
                <div class="row">
                    <div class="col-lg-8">
                        <div class="card mb-3">
                            <div class="card-header">
                                <strong>Product Types</strong>
                            </div>
                            <div class="card-body">
                                <v-client-table ref="productTypeTable" :columns="productTypeColumns" :options="productTypeOptions" :data="product_types">

                                    <div slot="afterFilter" class="ml-2">
                                        <router-link :to="`/admin/settings/product-types`" class="btn btn-sm btn-default">
                                            <i class="fa fa-plus-circle text-success"></i> Create
                                        </router-link>
                                    </div>

                                    <template slot="id" slot-scope="props">
                                        <router-link :to="`/admin/settings/product-types/${props.row.id}`" class="btn btn-xs btn-default" title="Edit">
                                            <i class="fas fa-sm fa-edit"></i>
                                        </router-link>
                                        <router-link :to="`/admin/settings/product-types/${props.row.id}/delete`" class="btn btn-xs btn-default" title="Delete">
                                            <i class="fas fa-sm fa-trash text-danger"></i>
                                        </router-link>
                                    </template>
                                </v-client-table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-show="curTab === 'codes'">
                <div class="row">
                    <div class="col-lg-8">
                        <div class="card mb-3">
                            <div class="card-header">
                                <strong>Do Not Contact Codes</strong>
                            </div>
                            <div class="card-body">
                                <v-client-table ref="assignedProductTable" :columns="dncColumns" :options="dncOptions" :data="codes">

                                    <div slot="afterFilter" class="ml-2">
                                        <router-link :to="`/admin/settings/do-not-contact-codes`" class="btn btn-sm btn-default">
                                            <i class="fa fa-plus-circle text-success"></i> Create
                                        </router-link>
                                    </div>

                                    <template slot="id" slot-scope="props">
                                        <router-link :to="`/admin/settings/do-not-contact-codes/${props.row.id}`" class="btn btn-xs btn-default" title="Edit"><i class="fas fa-sm fa-edit"></i></router-link>
                                    </template>

                                    <template slot="is_active" slot-scope="props">
                                        <label class="badge badge-success" v-if="props.row.is_active == 1">Active</label>
                                        <label class="badge badge-warning" v-else-if="props.row.is_active == 0">Inactive</label>
                                    </template>
                                </v-client-table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-show="curTab === 'marketer_codes'">
                <div class="row">
                    <div class="col-lg-8">
                        <div class="card mb-3">
                            <div class="card-header">
                                <strong>Marketer Relationship Codes</strong>
                            </div>
                            <div class="card-body">
                                <v-client-table ref="marketerRelCodes" :columns="marketerColumns" :options="marketerOptions" :data="marketerCodes">

                                    <div slot="afterFilter" class="ml-2">
                                        <router-link :to="`/admin/settings/marketer-codes`" class="btn btn-sm btn-default">
                                            <i class="fa fa-plus-circle text-success"></i> Create
                                        </router-link>
                                    </div>

                                    <template slot="id" slot-scope="props">
                                        <router-link :to="`/admin/settings/marketer-codes/${props.row.id}`" class="btn btn-xs btn-default" title="Edit"><i class="fas fa-sm fa-edit"></i></router-link>
                                    </template>

                                    <template slot="marketer_code_desc" slot-scope="props">
                                        <div v-html="$sanitize(props.row.marketer_code_desc)" />
                                    </template>

                                    <template slot="is_active" slot-scope="props">
                                        <label class="badge badge-success" v-if="props.row.is_active == 1">Active</label>
                                        <label class="badge badge-warning" v-else-if="props.row.is_active == 0">Inactive</label>
                                    </template>
                                </v-client-table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-show="curTab === 'contract-statuses'">
                <div class="card mb-3">
                    <div class="card-header">
                        <strong>Product Contract Statuses</strong>
                    </div>
                    <div class="card-body">
                        <v-client-table ref="prodContractStatusTable" :columns="contractStatusColumns" :options="contractStatusOptions" :data="contractStatuses">

                            <div slot="afterFilter" class="ml-2">
                                <router-link :to="`/admin/settings/contract-statuses`" class="btn btn-sm btn-default">
                                    <i class="fa fa-plus-circle text-success"></i> Create
                                </router-link>
                            </div>

                            <template slot="id" slot-scope="props">
                                <router-link :to="`/admin/settings/contract-statuses/${props.row.id}`" class="btn btn-xs btn-default" title="Edit"><i class="fas fa-sm fa-edit"></i></router-link>
                            </template>

                            <template slot="contract_status_desc" slot-scope="props">
                                <div v-html="$sanitize(props.row.contract_status_desc)" />
                            </template>

                            <template slot="contract_status_color" slot-scope="props">
                                <span :class="'badge badge-'+props.row.contract_status_color">Color</span>
                            </template>

                            <template slot="include_on_downline" slot-scope="props">
                                <label class="badge badge-success" v-if="props.row.include_on_downline == 1">Include on Downline</label>
                                <label class="badge badge-warning" v-else-if="props.row.include_on_downline == 0">Exclude on Downline</label>
                            </template>

                            <template slot="display_as_inactive" slot-scope="props">
                                <label class="badge badge-secondary" v-if="props.row.display_as_inactive == 1">Display as Inactive</label>
                                <label class="badge badge-success" v-else-if="props.row.display_as_inactive == 0">Display as Active</label>
                            </template>
                        </v-client-table>
                    </div>
                </div>
            </div>

            <div v-show="curTab === 'contract_defaults'">
                <div class="row">
                    <div class="col-lg-8">
                        <div class="card mb-3">
                            <div class="card-header">
                                <strong>Contract List Filter Defaults</strong>
                            </div>
                            <div class="card-body">
                                <p class="text-muted small mb-3">
                                    These filters are pre-applied when users first open the Contracts list. Users can clear or change them at any time.
                                </p>

                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right text-muted">Product:</div>
                                    <div class="col-md-8 col-12">
                                        <v-select v-model="contractFilterDefaults['product_contracts.product_id']" :options="products" :reduce="p => p.id" label="product_name" placeholder="— No Default —" multiple />
                                    </div>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right text-muted">Product Type:</div>
                                    <div class="col-md-8 col-12">
                                        <v-select v-model="contractFilterDefaults['products.product_type_id']" :options="product_types" :reduce="t => t.id" label="product_type_name" placeholder="— No Default —" multiple />
                                    </div>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right text-muted">Contract Status:</div>
                                    <div class="col-md-8 col-12">
                                        <v-select v-model="contractFilterDefaults['product_contracts.contract_status_id']" :options="contractStatuses" :reduce="s => s.id" label="contract_status_name" placeholder="— No Default —" multiple />
                                    </div>
                                </div>

                                <hr />

                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right text-muted">Created Date:</div>
                                    <div class="col-md-8 col-12">
                                        <b-form-select v-model="contractFilterDefaults['product_contracts.created_at']" :options="datePresetOptions" size="sm" />
                                    </div>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right text-muted">Last Updated:</div>
                                    <div class="col-md-8 col-12">
                                        <b-form-select v-model="contractFilterDefaults['product_contracts.updated_at']" :options="datePresetOptions" size="sm" />
                                    </div>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right text-muted">Date of Change:</div>
                                    <div class="col-md-8 col-12">
                                        <b-form-select v-model="contractFilterDefaults['product_contract_changes.date_of_change']" :options="datePresetOptions" size="sm" />
                                    </div>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right text-muted">Requested Date:</div>
                                    <div class="col-md-8 col-12">
                                        <b-form-select v-model="contractFilterDefaults['product_contracts.requested_date']" :options="datePresetOptions" size="sm" />
                                    </div>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right text-muted">Credentialing Date:</div>
                                    <div class="col-md-8 col-12">
                                        <b-form-select v-model="contractFilterDefaults['product_contracts.credentialing_date']" :options="datePresetOptions" size="sm" />
                                    </div>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right text-muted">Certification Date:</div>
                                    <div class="col-md-8 col-12">
                                        <b-form-select v-model="contractFilterDefaults['product_contracts.certification_date']" :options="datePresetOptions" size="sm" />
                                    </div>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right text-muted">Sent Date:</div>
                                    <div class="col-md-8 col-12">
                                        <b-form-select v-model="contractFilterDefaults['product_contracts.sent_date']" :options="datePresetOptions" size="sm" />
                                    </div>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-lg-3 col-md-4 col-12 text-md-right text-muted">Termination Date:</div>
                                    <div class="col-md-8 col-12">
                                        <b-form-select v-model="contractFilterDefaults['product_contracts.termination_date']" :options="datePresetOptions" size="sm" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button type="button" class="btn btn-sm btn-success" v-on:click="postContractDefaults()">
                            Save Contract Defaults
                        </button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
    import moment from 'moment-timezone';
    import { VueEditor, Quill } from "vue2-editor";

    export default {
        components: {
            VueEditor,
            Quill,
        },

        data() {
            return {
                loading: true,
                saving: false,
                curTab: 'account',
                errors: {},
                codes: [],
                product_types: [],
                marketerCodes: [],
                contractStatuses: [],
                products: [],
                settings: {
                    reply_to_email: null,
                },

                customToolbar: [
                    [
                        {
                            header: [false, 1, 2, 3, 4, 5, 6]
                        }
                    ],
                    ["bold", "italic", "underline", "strike"], // toggled buttons
                    [
                        {
                            align: ""
                        },
                        {
                            align: "center"
                        },
                        {
                            align: "right"
                        },
                        {
                            align: "justify"
                        }
                    ],
                    ["blockquote"],
                    [
                        {
                            list: "ordered"
                        },
                        {
                            list: "bullet"
                        },
                        {
                            list: "check"
                        }
                    ],
                    [
                        {
                            indent: "-1"
                        },
                        {
                            indent: "+1"
                        }
                    ], // outdent/indent
                    [
                        {
                            color: []
                        },
                        {
                            background: []
                        }
                    ], // dropdown with defaults from theme
                    ["link"],
                ],

                editorSettings: {
                    modules: {
                        toolbar: {
                            container: [
                                [
                                    {
                                        header: [false, 1, 2, 3, 4, 5, 6]
                                    }
                                ],
                                ["bold", "italic", "underline", "strike"], // toggled buttons
                                [
                                    {
                                        align: ""
                                    },
                                    {
                                        align: "center"
                                    },
                                    {
                                        align: "right"
                                    },
                                    {
                                        align: "justify"
                                    }
                                ],
                                ["blockquote"],
                                [
                                    {
                                        list: "ordered"
                                    },
                                    {
                                        list: "bullet"
                                    },
                                    {
                                        list: "check"
                                    }
                                ],
                                [
                                    {
                                        indent: "-1"
                                    },
                                    {
                                        indent: "+1"
                                    }
                                ], // outdent/indent
                                [
                                    {
                                        color: []
                                    },
                                    {
                                        background: []
                                    }
                                ], // dropdown with defaults from theme
                                ["link"], [] // remove formatting button
                            ],
                        },
                    },
                },

                productTypeColumns: [
                    'id',
                    'product_type_name',
                ],
                productTypeOptions: {
                    headings: {
                        id: 'Actions',
                        product_type_name: 'Name',
                    },
                    texts:{
                        filterPlaceholder:"Search Product Types",
                    },
                    orderBy: {
                        ascending: true,
                        column: 'product_type_name',
                    },
                    uniqueKey: 'id',
                },

                dncColumns: [
                    'id',
                    'dnc_code_reason',
                    'is_active'
                ],
                dncOptions: {
                    headings: {
                        id: 'Actions',
                        dnc_code_reason: 'Name',
                        is_active: 'Active',
                    },
                    texts:{
                        filterPlaceholder:"Search Do Not Contact Codes",
                    },
                    orderBy: {
                        ascending: true,
                        column: 'dnc_code_reason',
                    },
                    uniqueKey: 'id',
                },

                marketerColumns: [
                    'id',
                    'marketer_code_label',
                    'marketer_code_desc',
                    'is_active'
                ],
                marketerOptions: {
                    headings: {
                        id: 'Actions',
                        marketer_code_label: 'Code',
                        marketer_code_desc: 'Description',
                        is_active: 'Active',
                    },
                    texts:{
                        filterPlaceholder:"Search Relationship Codes",
                    },
                    orderBy: {
                        ascending: true,
                        column: 'marketer_code_label',
                    },
                    uniqueKey: 'id',
                },

                contractStatusColumns: [
                    'id',
                    'contract_status_name',
                    'contract_status_type',
                    'contract_status_color',
                    'contract_status_desc',
                    'include_on_downline',
                    'display_as_inactive',
                ],
                contractStatusOptions: {
                    skin: "table table-bordered table-sm table-hover",
                    headings: {
                        id: 'Actions',
                        contract_status_name: 'Status Name',
                        contract_status_type: 'Status Type',
                        contract_status_color: 'Status Color',
                        contract_status_desc: 'Description',
                        include_on_downline: 'Include on Downline',
                        display_as_inactive: 'Display on Profile',
                    },
                    texts:{
                        filterPlaceholder:"Search Contract Statuses",
                    },
                    orderBy: {
                        ascending: true,
                        column: 'contract_status_name',
                    },
                    uniqueKey: 'id',
                    rowClassCallback: row => {
                        return "alert-"+row.contract_status_color;
                    }
                },

                contractFilterDefaults: {
                    'product_contracts.product_id':                [],
                    'products.product_type_id':                    [],
                    'product_contracts.contract_status_id':        [],
                    'product_contracts.created_at':                null,
                    'product_contracts.updated_at':                null,
                    'product_contract_changes.date_of_change':     null,
                    'product_contracts.requested_date':            null,
                    'product_contracts.credentialing_date':        null,
                    'product_contracts.certification_date':        null,
                    'product_contracts.sent_date':                 null,
                    'product_contracts.termination_date':          null,
                },

                datePresetOptions: [
                    { value: null, text: '— No Default —' },
                    {
                        label: 'Calendar Year',
                        options: [
                            { value: 'ytd',          text: 'YTD: Jan. 1st - Current Date' },
                            { value: 'current_year', text: 'This Year' },
                        ]
                    },
                    {
                        label: 'Calendar Quarters',
                        options: [
                            { value: 'current_q1', text: 'Current Year - Q1' },
                            { value: 'current_q2', text: 'Current Year - Q2' },
                            { value: 'current_q3', text: 'Current Year - Q3' },
                            { value: 'current_q4', text: 'Current Year - Q4' },
                        ]
                    },
                    {
                        label: 'Calendar Month',
                        options: [
                            { value: 'month_last',    text: 'Last Month' },
                            { value: 'month_current', text: 'This Month' },
                            { value: 'month_next',    text: 'Next Month' },
                        ]
                    },
                    {
                        label: 'Calendar Week',
                        options: [
                            { value: 'calendar_last_week', text: 'Last Week' },
                            { value: 'calendar_this_week', text: 'This Week' },
                            { value: 'calendar_next_week', text: 'Next Week' },
                        ]
                    },
                    {
                        label: 'Days',
                        options: [
                            { value: 'yesterday',     text: 'Yesterday' },
                            { value: 'today',         text: 'Today' },
                            { value: 'last_7_days',   text: 'Last 7 Days' },
                            { value: 'last_30_days',  text: 'Last 30 Days' },
                            { value: 'last_60_days',  text: 'Last 60 Days' },
                            { value: 'last_90_days',  text: 'Last 90 Days' },
                            { value: 'last_120_days', text: 'Last 120 Days' },
                            { value: 'next_7_days',   text: 'Next 7 Days' },
                            { value: 'next_30_days',  text: 'Next 30 Days' },
                            { value: 'next_60_days',  text: 'Next 60 Days' },
                            { value: 'next_90_days',  text: 'Next 90 Days' },
                            { value: 'next_120_days', text: 'Next 120 Days' },
                        ]
                    },
                ],
            }
        },

        watch: {

        },

        computed: {
            tabs(){
                return [
                    {key: 'account', title: 'Account Settings', icon: 'cogs'},
                    {key: 'product_types', title: 'Product Types ('+this.product_types.length+')', icon: 'book'},
                    {key: 'codes', title: 'Do Not Contact Codes ('+this.codes.length+')', icon: 'unlink'},
                    {key: 'marketer_codes', title: 'Marketer Relationship Codes ('+this.marketerCodes.length+')', icon: 'sitemap'},
                    {key: 'contract-statuses', title: 'Product Contract Statuses ('+this.contractStatuses.length+')', icon: 'bolt'},
                    { key: 'contract_defaults', title: 'Contract List Defaults', icon: 'filter' },
                ];
            },

            tabTitle(){
                //Loop
                for (var i = this.tabs.length - 1; i >= 0; i--)
                {
                    //Check tabl
                    if(this.tabs[i].key === this.curTab)
                    {
                        return '<i class="fa fa-'+this.tabs[i].icon+'"></i> ' + this.tabs[i].title;
                    }
                }

                //Send it
                return '';
            },
        },

        methods: {
            getSettings(){
                //Send it
                this.$http.get('/admin/settings')
                    .then(response => {
                        //Set Data
                        this.settings          = response.data.account;
                        this.codes             = response.data.codes;
                        this.product_types     = response.data.productTypes;
                        this.marketerCodes     = response.data.marketerCodes;
                        this.contractStatuses  = response.data.contractStatuses;
                        this.products          = response.data.products;

                        if (response.data.account.contract_filter_defaults) {
                            this.contractFilterDefaults = Object.assign(
                                {},
                                this.contractFilterDefaults,
                                response.data.account.contract_filter_defaults
                            );
                        }
                    })
                    .catch(error => {

                    })
                    .then(() => {
                        //Reset
                        this.loading = false;
                    });
            },

            postSettings(){
                //Send it
                this.$http.post('/admin/settings', this.settings)
                    .then(response => {
                        //Alert the user
                        this.$toastr.s('Settings Saved');
                    })
                    .catch(error => {

                    })
                    .then(() => {
                        //Reset
                        this.loading = false;
                    });
            },

            postContractDefaults(){
                this.$http.post('/admin/contract-defaults', {
                    contract_filter_defaults: this.contractFilterDefaults,
                })
                .then(response => {
                    this.$toastr.s('Contract Defaults Saved');
                })
                .catch(error => {

                });
            },
        },

        mounted() {
            //Get the Data
            this.getSettings();

            //Check Has
            if(window.location.hash)
            {
                //Set the Tab
                this.curTab = window.location.hash.replace('#', '');
            }
        }
    }
</script>